<template>
  <div class="pb-beams-editor">
    <BeamsGridSize
      :columns="columns"
      :rows="rows"
      :min-allowed-columns="minColumns"
      :max-allowed-columns="maxColumns"
      :min-allowed-rows="minRows"
      :max-allowed-rows="maxRows"
      :disabled="disabled"
      @grid-size-changed="gridSizeChanged"
    />

    <UiField
      :label="translate('pb2.beamsEditor.editionModeLabel')"
      :disabled="disabled"
      class="pb-half-margin-bottom"
      :class="{ 'txt-muted': disabled }"
    >
      <AutoSpinner>
        <BaseSelect
          :options="clusterOptions"
          :value="clusterId"
          :model-modifiers="{ lazy: true }"
          alignment="right"
          :aria-label="translate('pb2.beamsEditor.editionModeLabel')"
          @input="clusterId = $event"
        >
          <template #toggler="togglerProps">
            <SelectDropdownToggler
              v-bind="togglerProps"
              key="toggler"
            >
              <template #default="{ label }">
                <template v-if="clusterIsSelected">
                  <Asset
                    :src="togglerProps.selection.asset"
                    placeload-size="xsmall"
                    placeload-radius="xs"
                    style="max-width: 1em; max-height: 1em; margin-right: 5px;"
                  />
                  {{ translate('pb2.beamsEditor.editClusterLabel').template({ name: label }) }}
                </template>
                <template v-else>
                  {{ label }}
                </template>
                <UiIcon
                  icon="arrowdown"
                  size="small"
                  class="ui-select-dropdown-toggler-icon"
                />
              </template>
            </SelectDropdownToggler>
          </template>
        </BaseSelect>
      </AutoSpinner>
    </UiField>

    <PbInput
      v-if="clusterIsSelected"
      v-model="selectedClusterName"
      :disabled="disabled"
      :loading="loading"
      :label="translate('pb2.beamsEditor.clusterNameLabel')"
      dynamic
      :placeholder="defaultClusterName"
      class="pb-half-margin-bottom"
      maxlength="20"
    />

    <BeamsGrid
      mode="edit"
      :columns-count="columns"
      :rows-count="rows"
      :beams="clusterBeams"
      :loading="loading"
      :disabled="disabled"
      @change="toggleBeamCluster"
    />
  </div>
</template>

<script setup lang="ts">
// name: 'BeamsEditor'

import { toRefs } from 'vue'

import { IDLE } from '@nicolaudie-group/ng-ui-kit/helpers/status'
import useStatus from '@nicolaudie-group/ng-ui-kit/composables/useStatus'

import Asset from '@nicolaudie-group/ng-ui-kit/components/Asset/Asset.vue'
import UiIcon from '@nicolaudie-group/ng-ui-kit/components/UiIcon/UiIcon.vue'
import UiField from '@nicolaudie-group/ng-ui-kit/components/UiField/UiField.vue'
import BaseSelect from '@nicolaudie-group/ng-ui-kit/components/Select/BaseSelect.vue'
import AutoSpinner from '@nicolaudie-group/ng-ui-kit/components/AutoSpinner/AutoSpinner.vue'
import SelectDropdownToggler from '@nicolaudie-group/ng-ui-kit/components/Select/SelectDropdownToggler.vue'

import useTranslate from '@/composables/useTranslate'

import PbInput from '../PbInput.vue'
import BeamsGrid from './BeamsGrid.vue'
import BeamsGridSize from './BeamsGridSize.vue'
import type { Beam, Cluster } from './beamsTypes.ts'
import {
  useBeamsClusterEditor,
  useBeamsGridSize,
} from './beamsEditorComposables.ts'

interface Props {
  /**
   * Status of the requests related to the event emitted by this component
   */
  // TODO: infer from UI kit "status" helper
  status?: 'SUCCESS' |'ERROR' |'PENDING' |'IDLE'
  /**
   * Disable the beams edition
   */
  disabled?: boolean
}

defineOptions({
  compatConfig: {
    MODE: 3, // opt-in to Vue 3 behavior for this component only
  }
})

const props = withDefaults(defineProps<Props>(), {
  status: IDLE,
  disabled: false,
})

/**
 * Cluster names indexed by ID
 */
const clusters = defineModel<Cluster[]>('clusters', {
  default: (): Cluster[] => [],
})

/**
 * List of beams to be parsed
 */
const beams = defineModel<Beam[]>('beams', {
  default: (): Beam[] => [],
})

const { translate } = useTranslate()

const { status } = toRefs(props)
const { pending: loading } = useStatus(status)

/**
 * Retrieve beams from props + watch and emit changes to make this component fully
 * controlled by parent component like any input via v-model
 */
const {
  clusterBeams,
  clusterOptions,
  clusterIsSelected,
  clusterId,
  defaultClusterName,
  selectedClusterName,
  toggleBeamCluster,
} = useBeamsClusterEditor(beams, clusters)

const {
  minColumns,
  maxColumns,
  columns,
  minRows,
  maxRows,
  rows,
  gridSizeChanged,
} = useBeamsGridSize(clusterBeams)
</script>

<style lang="scss">
.pb-beams-editor {
  .ui-listbox-option-asset {
    max-width: 1em;
    max-height: 1em;
  }
}
</style>
